import {useCallback, useEffect, useRef, useState} from 'react';

import {isNotNil} from 'ramda';

import {SECTION_SCROLL_OFFSET_PX} from '../constants/sectionIds';

interface UseScrollSpyOptions {
  readonly topOffset?: number;
  readonly bottomOffset?: number;
}

interface UseScrollSpyResult {
  readonly activeId: string | null;
  readonly lockActive: (id: string) => void;
}

const DEFAULT_BOTTOM_OFFSET = 240;
const PAGE_BOTTOM_EPSILON = 4;

const SCROLL_RELEASING_KEYS = new Set<string>([
  'PageUp',
  'PageDown',
  'Home',
  'End',
  'ArrowUp',
  'ArrowDown',
  ' ',
  'Spacebar',
]);

function isAtPageBottom(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const scrolled = window.scrollY + window.innerHeight;
  return scrolled >= document.documentElement.scrollHeight - PAGE_BOTTOM_EPSILON;
}

/**
 * Tracks which of `sectionIds` is currently "active" via IntersectionObserver.
 *
 * - At page-bottom we pick the bottom-most intersecting section so the last
 *   anchor click wins when several sections share the active band.
 * - `lockActive(id)` pins the active id and is released on the next user
 *   scroll input — prevents flapping during smooth-scroll settling.
 */
export function useScrollSpy(
  sectionIds: readonly string[],
  options: UseScrollSpyOptions = {}
): UseScrollSpyResult {
  const topOffset = options.topOffset ?? SECTION_SCROLL_OFFSET_PX;
  const bottomOffset = options.bottomOffset ?? DEFAULT_BOTTOM_OFFSET;
  // Lazy-initialize once so the first render already has a non-null activeId
  // when sectionIds is populated. Falling back to sectionIds[0] in the return
  // value handles the late-arriving-sections case (e.g. predictions resolve
  // after mount) without setState-during-render.
  const [activeId, setActiveId] = useState<string | null>(() => sectionIds[0] ?? null);

  const lockedIdRef = useRef<string | null>(null);
  const sectionIdsRef = useRef(sectionIds);
  sectionIdsRef.current = sectionIds;

  const sectionsKey = sectionIds.join('\u0000');

  const lockActive = useCallback((id: string) => {
    lockedIdRef.current = id;
    setActiveId(id);
  }, []);

  // eslint-disable-next-line no-restricted-syntax -- IntersectionObserver + scroll listeners need cleanup; useOnMount does not support it
  useEffect(
    function observeSections() {
      if (typeof window === 'undefined') {
        return undefined;
      }

      const ids = sectionIdsRef.current;
      if (ids.length === 0) {
        return undefined;
      }

      const elements = ids
        .map((id) => document.getElementById(id))
        .filter((el): el is HTMLElement => isNotNil(el));

      if (elements.length === 0) {
        return undefined;
      }

      const rootMargin = `-${topOffset}px 0px -${bottomOffset}px 0px`;
      const intersectingTops = new Map<string, number>();

      const releaseLock = () => {
        lockedIdRef.current = null;
      };

      const releaseLockOnScrollKey = (event: KeyboardEvent) => {
        if (SCROLL_RELEASING_KEYS.has(event.key)) {
          lockedIdRef.current = null;
        }
      };

      window.addEventListener('scrollend', releaseLock);
      window.addEventListener('wheel', releaseLock, {passive: true});
      window.addEventListener('touchmove', releaseLock, {passive: true});
      window.addEventListener('keydown', releaseLockOnScrollKey);

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const id = entry.target.id;
            if (entry.isIntersecting) {
              intersectingTops.set(id, entry.boundingClientRect.top);
            } else {
              intersectingTops.delete(id);
            }
          });

          if (lockedIdRef.current != null) {
            return;
          }

          if (intersectingTops.size === 0) {
            return;
          }

          const preferBottom = isAtPageBottom();
          let chosenId: string | null = null;
          let chosenY = preferBottom ? Number.NEGATIVE_INFINITY : Number.POSITIVE_INFINITY;

          intersectingTops.forEach((top, id) => {
            const isBetter = preferBottom ? top > chosenY : top < chosenY;
            if (isBetter) {
              chosenY = top;
              chosenId = id;
            }
          });

          if (isNotNil(chosenId)) {
            setActiveId(chosenId);
          }
        },
        {rootMargin, threshold: 0}
      );

      elements.forEach((el) => observer.observe(el));

      return () => {
        observer.disconnect();
        window.removeEventListener('scrollend', releaseLock);
        window.removeEventListener('wheel', releaseLock);
        window.removeEventListener('touchmove', releaseLock);
        window.removeEventListener('keydown', releaseLockOnScrollKey);
      };
    },
    [sectionsKey, topOffset, bottomOffset]
  );

  // Derive the displayed activeId so late-arriving sectionIds still default to
  // the first section without a render-phase setState.
  const displayedActiveId = isNotNil(activeId) ? activeId : (sectionIds[0] ?? null);

  return {activeId: displayedActiveId, lockActive};
}
